import {
  AlertCircle,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CarFront,
  ChartColumn,
  ClipboardList,
  Dumbbell,
  FileText,
  Gauge,
  House,
  LifeBuoy,
  ListCheck,
  ListTodo,
  MessageSquare,
  OctagonAlert,
  ParkingCircle,
  Radio,
  Route,
  PhoneCall,
  Scale,
  Settings,
  Shield,
  ShieldCheck,
  Trees,
  TriangleAlert,
  Users,
  Volleyball,
  Waves,
  WavesLadder,
  Wrench,
} from "lucide-react";

export const PUBLIC_PATHS = [
  "/",
  "login",
  "reset",
  "logout",
  "forgot",
  "location-denied",
  "onelogin",
  "acknowledge-shift",
  "resident-claim",
] as const;

export const STATIC_ROUTES = [
  "home",
  "dispatchdesk",
  "dashboard",
  "dars",
  "reports",
  "briefings",
  "direct-messages",
  "users",
  "shifts",
  "properties",
  "dispatch-centers",
  "patrol-routes",
  "reportrequests",
  "shifts-history",
  "tasks",
  "settings",
  "company",
  "officer-on-guard",
  "panic-button-settings",
  "company-vehicles",
  "emergency-contacts",
  "panic-alerts",
  /** Admin support tools (e.g. view-as-user); not property-section gated */
  "support",
  "rbac-admin",
] as const;

export const PATH_KEYS = {
  incidents: "incidents",
  "property-inspection-reports": "propertyinspectionreport",
  poolaccesslogs: "poolaccesslogs",
  tennisaccesslogs: "tennisaccesslogs",
  gymaccesslogs: "gymaccesslogs",
  recreationaccesslogs: "recreationaccesslogs",
  dispatchlogs: "dispatchlogs",
  briefings: "briefings",
  "direct-messages": "directmessages",
  households: "households",
  safelistings: "safelistings",
  "stored-vehicles": "storedvehicles",
  citations: "citations",
  /** First URL segment `/vehicle-maintenance` → backend `sections[].key` slug */
  "vehicle-maintenance": "vehiclemaintenances",
  "company-vehicles": "companyvehicles",
  vehiclemaintenances: "vehiclemaintenances",
  "permanent-parking": "permanentparking",
  "parking-enforcement": "vehicles",
} as const;

const GROUP = {
  REPORT: "report",
  USER: "user",
  CLIENT: "client",
  PARKING: "parking",
  OFFICER: "officer",
  NOTIFICATION: "notification",
  ANALYTICS: "analytics",
  SETTINGS: "settings",
} as const;

export const SECTIONS_GROUP = {
  dars: GROUP.REPORT,
  incidents: GROUP.REPORT,
  propertyinspectionreport: GROUP.REPORT,
  poolaccesslogs: GROUP.REPORT,
  tennisaccesslogs: GROUP.REPORT,
  gymaccesslogs: GROUP.REPORT,
  recreationaccesslogs: GROUP.REPORT,
  dispatchlogs: GROUP.REPORT,
  properties: GROUP.SETTINGS,
  dispatchcenters: GROUP.SETTINGS,
  patrolroutes: GROUP.SETTINGS,
  pools: GROUP.SETTINGS,
  tennis: GROUP.SETTINGS,
  gym: GROUP.SETTINGS,
  recreation: GROUP.SETTINGS,
  households: GROUP.SETTINGS,
  shifts: GROUP.SETTINGS,
  reports: GROUP.CLIENT,
  vehicles: GROUP.CLIENT,
  users: GROUP.SETTINGS,
  safelistings: GROUP.PARKING,
  storedvehicles: GROUP.PARKING,
  citations: GROUP.PARKING,
  permanentparking: GROUP.PARKING,
  tasks: GROUP.SETTINGS,
  briefings: GROUP.NOTIFICATION,
  directmessages: GROUP.NOTIFICATION,
  vehiclemaintenances: GROUP.OFFICER,
  companyvehicles: GROUP.SETTINGS,
  reportrequests: GROUP.ANALYTICS,
} as const;

export const ICONS: Record<string, React.ElementType> = {
  home: House,
  dispatchdesk: Radio,
  dashboard: Gauge,
  dars: FileText,
  incidents: TriangleAlert,
  propertyinspectionreport: ClipboardList,
  poolaccesslogs: Waves,
  tennisaccesslogs: Volleyball,
  gymaccesslogs: Dumbbell,
  recreationaccesslogs: Trees,
  dispatchlogs: PhoneCall,
  briefings: Bell,
  directmessages: MessageSquare,
  reportrequests: ChartColumn,
  shifts: CalendarDays,
  households: Building2,
  users: Users,
  "rbac-admin": Shield,
  pools: WavesLadder,
  tennis: Volleyball,
  gym: Dumbbell,
  recreation: Trees,
  properties: BriefcaseBusiness,
  dispatchcenters: Radio,
  patrolroutes: Route,
  citations: Scale,
  safelistings: ListCheck,
  vehiclemaintenances: Wrench,
  parkingenforcement: CarFront,
  vehicles: CarFront,
  warnings: OctagonAlert,
  storedvehicles: OctagonAlert,
  permanentparking: ParkingCircle,
  propertyinspectionreports: ClipboardList,
  tasks: ListTodo,
  company: Settings,
  "officer-on-guard": ShieldCheck,
  "panic-button-settings": OctagonAlert,
  "panic-alerts": OctagonAlert,
  "company-vehicles": CarFront,
  "emergency-contacts": AlertCircle,
  "view-as-user": LifeBuoy,
} as const;

export const FEATURE_NAMES = {
  dispatchdesk: "Dispatch desk",
  dars: "DARs",
  incidents: "Incidents",
  propertyinspectionreport: "Inspection Reports",
  propertyinspectionreports: "Inspection Reports",
  poolaccesslogs: "Pool Access Logs",
  tennisaccesslogs: "Tennis Access Logs",
  gymaccesslogs: "Gym Access Logs",
  recreationaccesslogs: "Recreation Access Logs",
  dispatchlogs: "Dispatch Logs",
  briefings: "Briefings",
  directmessages: "Direct messages",
  reportrequests: "Report Requests",
  shifts: "Shifts",
  households: "Households",
  users: "Users",
  pools: "Pools",
  tennis: "Tennis courts",
  gym: "Gyms",
  recreation: "Recreation areas",
  properties: "Properties",
  dispatchcenters: "Dispatch Centers",
  patrolroutes: "Patrol Routes",
  citations: "Citations",
  safelistings: "Safe Listings",
  vehicles: "Vehicles",
  vehiclemaintenances: "Vehicle Maintenance",
  permanentparking: "Permanent Parking",
  storedvehicles: "Stored Vehicles",
  tasks: "Tasks",
  companyvehicles: "Company vehicles",
  warnings: "Warnings",
  "panic-button-settings": "Panic button",
  "panic-alerts": "Panic alerts",
} as const;
